import * as React from 'react';

/*
 * Keeps one part of the Mapping page (the map, the tract panel, the tract list) that throws while it
 * draws from taking the whole app down with it: that part shows a short note with Try again, and the
 * rest of the page keeps working. The console keeps what went wrong. A new resetKey (the next tract,
 * for the panel) starts the part over on its own.
 */
interface ISoftErrorBoundaryProps {
    /* The part, as the note names it: 'map', 'tract panel', 'tract list'. */
    what: string;
    resetKey?: string | number;
    /* Where the note sits in the page's layout, in place of the part. */
    className?: string;
}

interface ISoftErrorBoundaryState {
    failed: boolean;
}

export class SoftErrorBoundary extends React.Component<ISoftErrorBoundaryProps, ISoftErrorBoundaryState> {
    state: ISoftErrorBoundaryState = { failed: false };

    static getDerivedStateFromError(): ISoftErrorBoundaryState {
        return { failed: true };
    }

    componentDidCatch(error: Error, info: React.ErrorInfo) {
        console.log(`Mapping: the ${this.props.what} hit an error and was replaced by a note.`, error, info.componentStack);
    }

    componentDidUpdate(prev: ISoftErrorBoundaryProps) {
        if (this.state.failed && prev.resetKey !== this.props.resetKey) {
            this.setState({ failed: false });
        }
    }

    render() {
        if (!this.state.failed) {
            return this.props.children;
        }
        return (
            <div className={`soft-error ${this.props.className || ''}`} role="alert">
                <span className="soft-error-text">
                    The {this.props.what} hit a problem and couldn&apos;t be shown. The rest of the page still works.
                </span>
                <button type="button" className="soft-error-retry" onClick={() => this.setState({ failed: false })}>
                    Try again
                </button>
            </div>
        );
    }
}

export default SoftErrorBoundary;



// WEBPACK FOOTER //
// ./src/components/project/sections/GISComponents/SoftErrorBoundary.tsx